import { api, ApiError } from '../api.js'

const decimalInput = /^[0-9]{1,9}(?:[.,][0-9]{1,3})?$/
const decimalWire = /^[0-9]{1,9}(?:\.[0-9]{1,3})?$/
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const operationStates = [
  'PREVIEW', 'EXPIRED', 'CANCELLED', 'CONFIRMED', 'CONFLICT', 'REJECTED',
  'DISPATCHING', 'ACKNOWLEDGED', 'VERIFYING', 'VERIFIED', 'PARTIAL', 'FAILED', 'UNKNOWN',
]
const stepStates = ['PLANNED', 'DISPATCH_INTENT', 'SENT', 'ACKNOWLEDGED', 'VERIFYING', 'VERIFIED', 'REJECTED', 'FAILED', 'UNKNOWN', 'CANCELLED']
const resultStates = ['APPLIED', 'MISMATCH', 'ERROR', 'UNKNOWN']
const pollableStates = new Set(['CONFIRMED', 'DISPATCHING', 'ACKNOWLEDGED', 'VERIFYING'])

const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const validInteger = (value, minimum, maximum = Number.MAX_SAFE_INTEGER) => Number.isSafeInteger(value) && value >= minimum && value <= maximum
const responseError = () => new ApiError('Invalid PTS-2 management response', 502, 'pts2_management_response_invalid')
const queryError = () => new ApiError('Invalid PTS-2 management request', 400, 'pts2_management_query_invalid')

function positiveId(value) {
  if (!validInteger(Number(value), 1)) throw queryError()
  return Number(value)
}

function validUuid(value) {
  return typeof value === 'string' && uuidPattern.test(value)
}

function safeText(value, maximum, nullable = false) {
  if (nullable && value === null) return null
  if (typeof value !== 'string' || value.length > maximum) throw responseError()
  return value
}

function safeDate(value, nullable = false) {
  if (nullable && value === null) return null
  if (typeof value !== 'string' || !Number.isFinite(Date.parse(value))) throw responseError()
  return value
}

function safeChoice(value, allowed) {
  if (!allowed.includes(value)) throw responseError()
  return value
}

function safeDecimal(value) {
  if (typeof value !== 'string' || !decimalWire.test(value)) throw responseError()
  return value
}

export function normalizePriceInput(value) {
  if (typeof value !== 'string') return null
  const input = value.trim()
  if (!decimalInput.test(input)) return null
  let [whole, fraction = ''] = input.replace(',', '.').split('.')
  whole = whole.replace(/^0+(?=\d)/, '')
  fraction = fraction.replace(/0+$/, '')
  const normalized = fraction ? `${whole}.${fraction}` : whole
  return /^0(?:\.0*)?$/.test(normalized) ? null : normalized
}

function projectFuelGrade(value) {
  if (!isRecord(value) || !validInteger(value.fuelGradeId, 1, 10)) throw responseError()
  return {
    fuelGradeId: value.fuelGradeId,
    name: safeText(value.name, 40, true),
    confirmedPrice: safeDecimal(value.confirmedPrice),
  }
}

function uniqueFuelGrades(values, projector) {
  if (!Array.isArray(values) || values.length > 10) throw responseError()
  const seen = new Set()
  return values.map((value) => {
    const projected = projector(value)
    if (seen.has(projected.fuelGradeId)) throw responseError()
    seen.add(projected.fuelGradeId)
    return projected
  })
}

function projectPriceIntent(value) {
  if (!isRecord(value)) throw responseError()
  const fuelGrades = uniqueFuelGrades(value.fuelGrades, (item) => {
    if (!isRecord(item) || !validInteger(item.fuelGradeId, 1, 10)) throw responseError()
    return {
      fuelGradeId: item.fuelGradeId,
      name: safeText(item.name, 40, true),
      before: safeDecimal(item.before),
      after: safeDecimal(item.after),
    }
  })
  if (fuelGrades.length < 1) throw responseError()
  return { fuelGrades }
}

function projectPriceResult(value) {
  if (!isRecord(value) || !validInteger(value.fuelGradeId, 1, 10)) throw responseError()
  return {
    fuelGradeId: value.fuelGradeId,
    expectedPrice: safeDecimal(value.expectedPrice),
    confirmedPrice: value.confirmedPrice === null ? null : safeDecimal(value.confirmedPrice),
    state: safeChoice(value.state, resultStates),
    verifiedAt: safeDate(value.verifiedAt, true),
  }
}

function projectPricesTarget(value) {
  if (!isRecord(value) || !isRecord(value.prices) || !Array.isArray(value.selectedFuelGradeIds)) throw responseError()
  const entries = Object.entries(value.prices)
  if (entries.length < 1 || entries.length > 10) throw responseError()
  const prices = {}
  for (const [key, price] of entries) {
    const id = Number(key)
    if (!/^(?:[1-9]|10)$/.test(key) || !validInteger(id, 1, 10)) throw responseError()
    prices[key] = safeDecimal(price)
  }
  if (value.selectedFuelGradeIds.length < 1 || value.selectedFuelGradeIds.length > 10) throw responseError()
  const selectedFuelGradeIds = []
  const selected = new Set()
  for (const id of value.selectedFuelGradeIds) {
    if (!validInteger(id, 1, 10) || selected.has(id) || !Object.hasOwn(prices, String(id))) throw responseError()
    selected.add(id)
    selectedFuelGradeIds.push(id)
  }
  return { prices, selectedFuelGradeIds }
}

function projectStep(value) {
  if (!isRecord(value) || !validInteger(value.index, 0, 255) || value.resourceKind !== 'FUEL_GRADES') throw responseError()
  return {
    index: value.index,
    resourceKind: 'FUEL_GRADES',
    resourceId: safeText(value.resourceId, 64),
    state: safeChoice(value.state, stepStates),
    target: projectPricesTarget(value.target),
    verifiedAt: safeDate(value.verifiedAt, true),
    errorCode: safeText(value.errorCode, 64, true),
  }
}

function projectOperationSummary(value, stationId, controllerId) {
  if (!isRecord(value) || !validUuid(value.id) || value.stationId !== stationId || value.controllerId !== controllerId
    || value.kind !== 'PRICE_CHANGE' || typeof value.resolved !== 'boolean' || !validInteger(value.targetCount, 1, 256)
    || !validInteger(value.actorUserId, 1)) throw responseError()
  return {
    id: value.id,
    stationId,
    controllerId,
    kind: 'PRICE_CHANGE',
    state: safeChoice(value.state, operationStates),
    actorUserId: value.actorUserId,
    actorUsername: safeText(value.actorUsername, 120),
    createdAt: safeDate(value.createdAt),
    confirmedAt: safeDate(value.confirmedAt, true),
    completedAt: safeDate(value.completedAt, true),
    resolved: value.resolved,
    targetCount: value.targetCount,
  }
}

function projectOperation(value, stationId, controllerId) {
  const summary = projectOperationSummary(value, stationId, controllerId)
  const intent = projectPriceIntent(value.intent)
  if (!Array.isArray(value.steps) || value.steps.length > 256 || !Array.isArray(value.targetResults)) throw responseError()
  const steps = value.steps.map(projectStep)
  if (new Set(steps.map(({ index }) => index)).size !== steps.length) throw responseError()
  const targetResults = uniqueFuelGrades(value.targetResults, projectPriceResult)
  const intentById = new Map(intent.fuelGrades.map((item) => [item.fuelGradeId, item]))
  if (targetResults.length !== intent.fuelGrades.length || targetResults.some((item) => item.expectedPrice !== intentById.get(item.fuelGradeId)?.after)) {
    throw responseError()
  }
  return {
    ...summary,
    resolvedAt: safeDate(value.resolvedAt, true),
    intent,
    steps,
    targetResults,
  }
}

function stationQuery(stationId) {
  return new URLSearchParams({ stationId: String(positiveId(stationId)) })
}

export async function getPriceSnapshot(stationId, controllerId, { signal } = {}) {
  stationId = positiveId(stationId)
  controllerId = positiveId(controllerId)
  const payload = await api(`/pts2/controllers/${controllerId}/prices?${stationQuery(stationId)}`, { method: 'GET', signal })
  if (!isRecord(payload) || typeof payload.version !== 'string' || payload.version.length < 1 || payload.version.length > 128
    || typeof payload.fresh !== 'boolean' || typeof payload.complete !== 'boolean'
    || (payload.observedTransport !== null && !['WS', 'WSS'].includes(payload.observedTransport))
    || !isRecord(payload.capabilities) || typeof payload.capabilities.priceChange !== 'boolean') throw responseError()
  return {
    version: payload.version,
    fresh: payload.fresh,
    complete: payload.complete,
    observedTransport: payload.observedTransport,
    fuelGrades: uniqueFuelGrades(payload.fuelGrades, projectFuelGrade),
    capabilities: { priceChange: payload.capabilities.priceChange },
  }
}

export async function createPricePreview(stationId, controllerId, expectedVersion, changes, { signal } = {}) {
  stationId = positiveId(stationId)
  controllerId = positiveId(controllerId)
  if (typeof expectedVersion !== 'string' || expectedVersion.length < 1 || expectedVersion.length > 128
    || !Array.isArray(changes) || changes.length < 1 || changes.length > 10) throw queryError()
  const seen = new Set()
  const fuelGradeChanges = changes.map((item) => {
    if (!isRecord(item) || !validInteger(item.fuelGradeId, 1, 10) || seen.has(item.fuelGradeId)) throw queryError()
    const price = normalizePriceInput(item.price)
    if (price === null) throw queryError()
    seen.add(item.fuelGradeId)
    return { fuelGradeId: item.fuelGradeId, price }
  })
  const payload = await api(`/pts2/controllers/${controllerId}/operation-previews?${stationQuery(stationId)}`, {
    method: 'POST', signal,
    body: JSON.stringify({ kind: 'PRICE_CHANGE', expectedVersion, intent: { fuelGradeChanges } }),
  })
  if (!isRecord(payload) || !validUuid(payload.previewId) || payload.state !== 'PREVIEW'
    || !validInteger(payload.targetCount, 1, 256) || typeof payload.canConfirm !== 'boolean') throw responseError()
  return {
    previewId: payload.previewId,
    state: 'PREVIEW',
    expiresAt: safeDate(payload.expiresAt),
    intent: projectPriceIntent(payload.intent),
    targetCount: payload.targetCount,
    canConfirm: payload.canConfirm,
  }
}

export async function confirmPriceOperation(stationId, controllerId, previewId, idempotencyKey, { signal } = {}) {
  stationId = positiveId(stationId)
  controllerId = positiveId(controllerId)
  if (!validUuid(previewId) || !validUuid(idempotencyKey)) throw queryError()
  const payload = await api(`/pts2/controllers/${controllerId}/operations?${stationQuery(stationId)}`, {
    method: 'POST', signal, body: JSON.stringify({ previewId, idempotencyKey }),
  })
  return projectOperation(payload, stationId, controllerId)
}

export async function listPriceOperations(stationId, controllerId, { cursor, limit = 10, signal } = {}) {
  stationId = positiveId(stationId)
  controllerId = positiveId(controllerId)
  if (!validInteger(limit, 1, 200) || (cursor != null && (typeof cursor !== 'string' || cursor.length > 512))) throw queryError()
  const query = stationQuery(stationId)
  query.set('kind', 'PRICE_CHANGE')
  query.set('limit', String(limit))
  if (cursor) query.set('cursor', cursor)
  const payload = await api(`/pts2/controllers/${controllerId}/operations?${query}`, { method: 'GET', signal })
  if (!isRecord(payload) || !Array.isArray(payload.items) || payload.items.length > limit
    || (payload.nextCursor !== null && typeof payload.nextCursor !== 'string') || typeof payload.hasMore !== 'boolean') throw responseError()
  const items = payload.items.map((item) => projectOperationSummary(item, stationId, controllerId))
  if (new Set(items.map(({ id }) => id)).size !== items.length) throw responseError()
  return { items, nextCursor: payload.nextCursor, hasMore: payload.hasMore }
}

export async function getPriceOperation(stationId, controllerId, operationId, { signal } = {}) {
  stationId = positiveId(stationId)
  controllerId = positiveId(controllerId)
  if (!validUuid(operationId)) throw queryError()
  const payload = await api(`/pts2/controllers/${controllerId}/operations/${operationId}?${stationQuery(stationId)}`, { method: 'GET', signal })
  return projectOperation(payload, stationId, controllerId)
}

export async function reconcilePriceOperation(stationId, controllerId, operationId, { signal } = {}) {
  stationId = positiveId(stationId)
  controllerId = positiveId(controllerId)
  if (!validUuid(operationId)) throw queryError()
  const payload = await api(`/pts2/controllers/${controllerId}/operations/${operationId}/reconcile?${stationQuery(stationId)}`, {
    method: 'POST', signal,
  })
  return projectOperation(payload, stationId, controllerId)
}

export function isOperationPollable(operation) {
  return pollableStates.has(operation?.state)
}
